import Check from '@mui/icons-material/Check'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { CustomVariable, CustomVariableValue } from '@/types/custom-variables'
import { Organization } from '@/types/organizations'
import ExcludedVendorsField from './ExcludedVendorsField'
import POInstructionsField from './POInstructionsField'

type DataProviderOrgPOCardProps = {
  dataProviderOrg: Organization
  performMatchVariable: CustomVariable
  instructionsVariable: CustomVariable
  excludedVendorsVariable: CustomVariable
  orgCredentialTypeId: string
  performMatchValue?: CustomVariableValue
  instructionsValue?: CustomVariableValue
  excludedVendorsValue?: CustomVariableValue
  onValueChange: (
    variableValue: Omit<CustomVariableValue, 'id' | 'project_id'> & {
      id?: string
    },
  ) => void
}

export default function DataProviderOrgPOCard({
  dataProviderOrg,
  performMatchVariable,
  instructionsVariable,
  excludedVendorsVariable,
  orgCredentialTypeId,
  performMatchValue,
  instructionsValue,
  excludedVendorsValue,
  onValueChange,
}: DataProviderOrgPOCardProps) {
  const isMatchOn = !performMatchValue || performMatchValue.value === 'true'

  return (
    <Accordion
      disableGutters
      variant="outlined"
      sx={{
        borderRadius: 1,
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', pr: 1 }}
        >
          <Typography sx={{ fontWeight: 500 }}>
            {dataProviderOrg.name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={isMatchOn ? 'success' : 'default'}
              icon={isMatchOn ? <Check /> : undefined}
              label={isMatchOn ? 'PO Match On' : 'PO Match Off'}
              variant={isMatchOn ? 'filled' : 'outlined'}
            />
            <Switch
              checked={isMatchOn}
              onClick={(e) => e.stopPropagation()}
              onChange={(_e, checked) => {
                onValueChange({
                  ...performMatchValue,
                  custom_variable_id: performMatchVariable.id,
                  owner_org_id: dataProviderOrg.id,
                  value: checked ? 'true' : 'false',
                })
              }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <POInstructionsField
            label={`${dataProviderOrg.name} Match Instructions`}
            value={instructionsValue?.value ?? ''}
            onChange={(instructions) => {
              onValueChange({
                ...instructionsValue,
                custom_variable_id: instructionsVariable.id,
                owner_org_id: dataProviderOrg.id,
                value: instructions,
              })
            }}
            helperText={instructionsVariable.description || ''}
          />
          <ExcludedVendorsField
            orgId={dataProviderOrg.id}
            orgCredentialTypeId={orgCredentialTypeId}
            value={excludedVendorsValue?.value ?? ''}
            onChange={(excludedVendorNumbers) => {
              onValueChange({
                ...excludedVendorsValue,
                custom_variable_id: excludedVendorsVariable.id,
                owner_org_id: dataProviderOrg.id,
                value: excludedVendorNumbers,
              })
            }}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
